import {
  MFAChannel,
  PreferenceSelectorState,
} from '@eCommerce/frontend-legacy-shared-library';
import {yupResolver} from '@hookform/resolvers/yup';
import {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';

import FormField from 'components/FormField';
import PanelContainer, {BoldedText} from 'components/PanelContainer';
import {useDismissBehavior} from 'hooks/useDismissBehavior';
import {useMaskPhoneNumber} from 'hooks/useMaskPhoneNumber';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

import {addEmailValidation} from './addEmailValidation';
import {addPhoneNumberValidation} from './addPhoneValidation';

// CSS class for uppercase labels only
const labelClassName = 'uppercase-label';

// Add CSS to head if not already present
if (
  typeof document !== 'undefined' &&
  !document.getElementById('uppercase-label-style')
) {
  const style = document.createElement('style');
  style.id = 'uppercase-label-style';
  style.textContent = `
    .uppercase-label label {
      text-transform: uppercase !important;
    }
  `;
  document.head.appendChild(style);
}

const EMAIL_BODY_TEXT = {
  BODY_TEXT_FIRST: 'Thank you for validating your phone number.',
  BODY_TEXT_SECOND:
    "Now let's add a security email address to your account. We'll send you an email with a confirmation link so we can verify that it's you.",

  BODY_TEXT_DEFAULT: 'Add an email address to your account.',
};

const PHONE_BODY_TEXT = {
  BODY_TEXT_FIRST: 'Thank you for validating your email address.',
  BODY_TEXT_SECOND:
    "Now let's add a phone number to your account. We'll send you an SMS text message with a confirmation code so we can verify that it's you.",

  BODY_TEXT_DEFAULT:
    ' Add a phone number to your account. Must be a U.S. or Canadian mobile number. Standard message and data rates may apply.',
};

export interface AddEmailProps {
  onSend: (email: string) => void;
  preferenceSelectorState: PreferenceSelectorState;
  isLoading: boolean;
  channel: MFAChannel;
  // if user input matches the value on file
  isDuplicate: boolean;
  clearDuplicateError: () => void;
}

const AddContactInfo: React.FunctionComponent<AddEmailProps> = (props) => {
  const {
    onSend,
    preferenceSelectorState,
    isLoading,
    channel,
    isDuplicate,
    clearDuplicateError,
  } = props;

  const onCancelPress = useDismissBehavior();
  const {maskNumber, unmaskNumber} = useMaskPhoneNumber();
  const {trackAdobeEvent} = useTrackEvent();

  const isEmail = channel === MFAChannel.EMAIL;
  const {BODY_TEXT_DEFAULT} = isEmail ? EMAIL_BODY_TEXT : PHONE_BODY_TEXT;

  const {
    handleSubmit,
    control,
    formState: {errors},
  } = useForm({
    defaultValues: isEmail ? {email: ''} : {phoneNumber: '+1 '},
    resolver: yupResolver(
      isEmail ? addEmailValidation : addPhoneNumberValidation,
    ),
    mode: 'onSubmit',
  });

  useEffect(() => {
    trackAdobeEvent({
      event: AdobeEvent.pageLoad,
      pageLoad: {
        name: 'mfa contact info entry',
      },
    });
  }, []);

  const handleAdobeClickEvent = () => {
    let eventName = '';
    switch (preferenceSelectorState) {
      case PreferenceSelectorState.AddEmail:
        eventName = 'mfa add email';
        break;
      case PreferenceSelectorState.EditEmail:
        eventName = 'mfa edit email';
        break;
      case PreferenceSelectorState.AddPhone:
        eventName = 'mfa add phone';
        break;
      case PreferenceSelectorState.EditPhone:
        eventName = 'mfa edit phone';
        break;
      default:
        break;
    }

    trackAdobeEvent({event: AdobeEvent.click, click: {name: eventName}});
  };

  const onSendPress = () => {
    handleAdobeClickEvent();
    handleSubmit((data) => {
      const payload = isEmail ? data.email : unmaskNumber(data.phoneNumber);
      onSend(payload || '');
    })();
  };

  const getButtonTitle = () => {
    switch (preferenceSelectorState) {
      case PreferenceSelectorState.AddEmail:
        return 'Add Email Address';
      case PreferenceSelectorState.EditEmail:
        return 'Update Email Address';
      case PreferenceSelectorState.AddPhone:
        return 'Add Phone Number';
      case PreferenceSelectorState.EditPhone:
        return 'Update Phone Number';
      default:
        return undefined;
    }
  };

  const duplicateErrorMsg = `The ${
    isEmail ? 'email' : 'phone number'
  } you entered is already associated with your account. Please verify your entry and try again, or cancel your changes`;

  return (
    <PanelContainer
      isLoading={isLoading}
      bodyText={BODY_TEXT_DEFAULT}
      primaryTitle={getButtonTitle()}
      onPrimaryPress={onSendPress}
      secondaryTitle="Cancel"
      onSecondaryPress={onCancelPress}
      title="Verify Your Identity"
    >
      {isEmail ? (
        <Controller
          name="email"
          control={control}
          render={({field}) => (
            <div className={labelClassName}>
              <FormField
                autoFocus={true}
                editable={true}
                label="Email address"
                isInvalid={!!errors.email || isDuplicate}
                isInvalidLabel={
                  (errors?.email?.message as string) || duplicateErrorMsg
                }
                nativeID=""
                testID="email-input-field"
                onChangeText={(text) => {
                  field.onChange(text);
                  clearDuplicateError();
                }}
                value={field.value}
              />
            </div>
          )}
        />
      ) : (
        <Controller
          name="phoneNumber"
          control={control}
          render={({field}) => (
            <div className={labelClassName}>
              <FormField
                autoFocus={true}
                editable={true}
                label="Phone number"
                isInvalid={!!errors.phoneNumber || isDuplicate}
                isInvalidLabel={
                  (errors?.phoneNumber?.message as string) || duplicateErrorMsg
                }
                nativeID=""
                testID="phoneNumber-input-field"
                onChangeText={(text) => {
                  field.onChange(maskNumber(text)), clearDuplicateError();
                }}
                value={field.value}
              />
            </div>
          )}
        />
      )}
    </PanelContainer>
  );
};

export default AddContactInfo;
